import React, { useState, useEffect, useMemo } from 'react';
import { SunoClip, ParsedSunoOutput } from '@/types';
import { Music, Search, Check, RefreshCw } from 'lucide-react';
import { Modal, Input, Button, Badge } from '../ui';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { ArtThumb } from '@components/ArtThumb';

interface VaultPickerProps {
  isOpen: boolean;
  onClose: () => void;
  history: SunoClip[];
  onSelect: (clip: SunoClip) => void;
  slotLabel: 'A' | 'B';
}

function getSourceBadgeVariant(clip: SunoClip): 'primary' | 'warning-muted' | 'info-muted' | 'default-muted' {
  const ext = clip as any;
  if (ext._sourceType === 'suite') return 'info-muted';
  if (ext._sourceType === 'regenerated') return 'warning-muted';
  if (ext._sourceType === 'generated') return 'primary';
  if (clip.id.startsWith('draft_')) return 'warning-muted';
  if (clip.id.startsWith('upload_')) return 'info-muted';
  return 'default-muted';
}

function getSourceLabel(clip: SunoClip): string {
  const ext = clip as any;
  if (ext._artist?.startsWith('via ')) return ext._artist.replace('via ', '');
  if (ext._sourceType === 'suite') return 'Suite';
  if (ext._sourceType === 'regenerated') return 'Revised';
  if (ext._sourceType === 'generated') return 'Generated';
  if (clip.id.startsWith('draft_')) return 'Draft';
  if (clip.id.startsWith('upload_')) return 'Upload';
  return 'Sync';
}

export const VaultPicker: React.FC<VaultPickerProps> = ({
  isOpen,
  onClose,
  history,
  onSelect,
  slotLabel
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [vaultDrafts, setVaultDrafts] = useState<SunoClip[]>([]);
  const [vaultLoading, setVaultLoading] = useState(false);

  // Fetch vault drafts from DB when picker opens
  useEffect(() => {
    if (!isOpen) return;
    setVaultLoading(true);
    fetchWithCsrf(API_ENDPOINTS.drafts.list)
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then((drafts: any[]) => {
        const clips: SunoClip[] = [];
        for (const draft of drafts) {
          if (!draft.lyricsWithTags && !draft.originalData) continue;
          let parsed: ParsedSunoOutput | undefined;
          if (draft.originalData) {
            try { parsed = JSON.parse(draft.originalData); } catch { /* skip */ }
          }
          clips.push({
            id: `draft_${draft.id}`,
            title: draft.title,
            created_at: draft.createdAt,
            model_name: draft.artist || draft.sourceType,
            imageUrl: draft.albumArtUrl || undefined,
            metadata: {
              tags: draft.style || null,
              prompt: draft.lyricsWithTags || null,
            },
            originalData: parsed,
            _sourceType: draft.sourceType,
            _artist: draft.artist,
          } as SunoClip & { _sourceType?: string; _artist?: string });
        }
        setVaultDrafts(clips);
      })
      .catch((err: unknown) => console.error('[VaultPicker] Failed to fetch drafts:', err))
      .finally(() => setVaultLoading(false));
  }, [isOpen]);

  // Merge vault drafts + suno history, deduplicate by ID
  const allClips = useMemo(() => {
    const seen = new Set<string>();
    const merged: SunoClip[] = [];
    // Vault drafts first (generated content)
    for (const clip of vaultDrafts) {
      if (!seen.has(clip.id)) {
        seen.add(clip.id);
        merged.push(clip);
      }
    }
    // Then suno-synced clips
    for (const clip of history) {
      if (!seen.has(clip.id)) {
        seen.add(clip.id);
        merged.push(clip);
      }
    }
    return merged;
  }, [vaultDrafts, history]);

  // A Suno-synced clip (or a stored one) can arrive without a title even though the type says it has one;
  // one such clip used to crash the whole picker (AUTO-095).
  const needle = searchTerm.toLowerCase();
  const filtered = allClips.filter(c =>
    (c.title ?? '').toLowerCase().includes(needle) ||
    (c.metadata?.tags?.toLowerCase().includes(needle))
  );

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg ${slotLabel === 'A' ? 'bg-blue-500/20' : 'bg-amber-500/20'} flex items-center justify-center ${slotLabel === 'A' ? 'text-blue-400' : 'text-amber-400'} font-black text-xs`}>
        {slotLabel}
      </div>
      <div>
        <span className="text-xl font-black text-slate-50 uppercase tracking-tighter">
          Pick Song for Slot {slotLabel}
        </span>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">Select an architecture from your vault</p>
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex justify-between items-center">
      <p className="text-[11px] text-slate-400 font-bold uppercase">
        {filtered.length} Items Found
      </p>
      <Button
        variant="secondary"
        size="sm"
        onClick={onClose}
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
      footer={footerContent}
      className="max-h-[85vh] flex flex-col rounded-3xl"
      overlayClassName="z-[130]"
    >
      {/* Custom Header */}
      <div className="px-2 pb-6 border-b border-slate-800 -mt-2 mb-4">
        {headerContent}
      </div>

      {/* Search Bar */}
      <div className="pb-6 border-b border-slate-800 mb-4">
        <Input
          placeholder="Search by title or style tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftElement={<Search size={18} className="text-slate-400" />}
          autoFocus
          className="rounded-2xl py-3.5"
        />
      </div>

      {/* List */}
      <div className="flex-grow overflow-y-auto custom-scrollbar -mx-2 px-2">
        {vaultLoading ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400">
            <RefreshCw size={20} className="animate-spin mb-3" />
            <p className="text-sm font-bold uppercase tracking-widest">Loading vault...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-700">
            <Music size={48} className="opacity-20 mb-4" />
            <p className="font-black uppercase tracking-widest text-sm">No matches in Vault</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(clip => (
              <button
                key={clip.id}
                onClick={() => onSelect(clip)}
                className="group relative flex items-center gap-4 p-4 bg-slate-950/40 border border-slate-800 rounded-2xl text-left hover:border-accent/50 hover:bg-slate-800/40 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center flex-shrink-0 text-slate-700 border border-slate-800 overflow-hidden">
                  {clip.imageUrl ? (
                    <ArtThumb url={clip.imageUrl} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                  ) : (
                    <Music size={20} />
                  )}
                </div>
                <div className="min-w-0 flex-grow">
                  <h4 className="text-sm font-black text-slate-200 truncate group-hover:text-accent transition-colors">
                    {clip.title || 'Untitled'}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      variant={getSourceBadgeVariant(clip)}
                      size="xs"
                      className="font-black uppercase"
                    >
                      {getSourceLabel(clip)}
                    </Badge>
                    <span className="text-[11px] text-slate-400 font-mono">
                      {new Date(clip.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity pr-2">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-slate-950 shadow-lg">
                    <Check size={16} strokeWidth={3} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
